"use client";

// EVERY POPUP'S BEHAVIOUR, ONCE — the backdrop, Escape, a click outside, the
// keyboard kept inside the box, and focus in on the way up and back out on the
// way down. What goes inside the box, heading and all, is the caller's.
//
// ⚠️ WHY THIS EXISTS (2026-09-18). The app had one shared popup, PopupShell, and
// five others that each drew their own backdrop because PopupShell could only
// be closed by a LINK: upload footage, the Slack notice after a submit, the
// comment delete confirm, the write-failure notice and First 30's confirm. Four
// of the five did not close on Escape, and the one that did carried its own
// eighty lines of focus handling. Jonathan found it on the upload box: "This
// should be a standard behavior that's just available for all popup boxes."
//
// So the behaviour moved here and PopupShell is now this plus its header and a
// link. A new popup gets all of it by wrapping its box in <DialogFrame>. Do not
// draw a `fixed inset-0` backdrop by hand again.
//
// ⚠️ ONLY THE TOP ONE LISTENS. Popups stack — the write-failure notice can come
// up over the upload box it interrupted — and every frame listens on the
// document. One Escape closes one popup: the one on top.

import { useEffect, useLayoutEffect, useRef, useState, type ReactNode, type RefObject } from "react";
import clsx from "clsx";

/** Everything a Tab can land on inside the box. */
const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** Open frames, oldest first. The last one is the one the keyboard talks to. */
const stack: object[] = [];

export function DialogFrame({
  onClose,
  closable = true,
  role = "dialog",
  labelledBy,
  describedBy,
  initialFocus,
  returnFocusTo,
  fallbackFocusRef,
  layer = "z-50",
  placement = "center",
  backdropTestId,
  testId,
  className,
  edges,
  children,
}: {
  /** Escape and a click on the backdrop. Read at the moment of the key, never the one it opened with. */
  onClose: () => void;
  /** False while the popup is mid-action (a delete in flight): Escape and the backdrop do nothing. */
  closable?: boolean;
  /** `alertdialog` for an interruption that needs an answer. */
  role?: "dialog" | "alertdialog";
  labelledBy?: string;
  describedBy?: string;
  /**
   * WHERE THE CARET GOES ON OPENING: a selector inside the box, or a ref.
   * Default is the box itself, so a screen reader reads the heading before any
   * control. A control that already took focus (an `autoFocus` input) keeps it.
   */
  initialFocus?: string | RefObject<HTMLElement | null>;
  /**
   * WHERE FOCUS GOES BACK TO, as a selector — for a popup opened by a link,
   * whose opener is found again rather than remembered (PopupShell's
   * `data-opens`). Default: whatever had focus when this opened.
   */
  returnFocusTo?: string;
  /** Used when the thing that opened this is gone by the time it closes (a delete removed its own row). */
  fallbackFocusRef?: RefObject<HTMLElement | null>;
  /** The stacking layer. The write-failure notice sits above everything else. */
  layer?: "z-50" | "z-[60]";
  /** Where the box sits: the middle of the screen, or down the right edge, full height (components/drawer.tsx). */
  placement?: "center" | "right";
  backdropTestId?: string;
  testId?: string;
  /** The box: width, border, padding, radius. */
  className?: string;
  /**
   * BESIDE THE BOX, NOT IN IT: what sits on its left and right edges (an item
   * popup's Previous and Next), so it never covers the box's own content and
   * the box's scrolling never clips it. Clicks on them never close the popup:
   * only the backdrop itself does. Centred popups only. The backdrop is their
   * positioned box, so a caller may pin them over the box's edges instead (on
   * a phone, where the room beside it would squeeze the box).
   */
  edges?: { left: ReactNode; right: ReactNode };
  children: ReactNode;
}) {
  const box = useRef<HTMLDivElement>(null);
  // This frame's place in the stack: one identity per opening.
  const [token] = useState(() => ({}));

  // WHAT HAD FOCUS BEFORE THIS OPENED — read on the FIRST RENDER, and no later.
  // React focuses an `autoFocus` child while it commits, before any effect of
  // this component runs, so an effect that asked "what is focused?" was told
  // "the input inside me" and, on closing, returned focus to a node that no
  // longer existed: the page body (the upload box, 2026-09-18 walkthrough).
  // `typeof document` because a link-opened popup can render on the server.
  const [opener] = useState(() =>
    typeof document !== "undefined" && document.activeElement instanceof HTMLElement ? document.activeElement : null,
  );

  // THE LATEST PROPS, for handlers bound once per opening. Written after commit,
  // so a render React throws away cannot leave Escape holding props that never
  // reached the screen — and a dialog that stops being busy closes through the
  // handler it has now.
  const latest = useRef({ onClose, closable });
  useLayoutEffect(() => {
    latest.current = { onClose, closable };
  });

  // THE KEYBOARD. Escape closes; Tab cycles inside the box rather than walking
  // off into a page the backdrop has covered.
  //
  // ⚠️ KEEP THIS EFFECT ABOVE THE FOCUS ONE. Effects run in declaration order,
  // so "focus is inside the box" also means "Escape is being listened for" — and
  // `pressEscapeOnPopup` in tests/e2e/_helpers.ts waits on exactly that.
  useEffect(() => {
    stack.push(token);
    const onKey = (e: KeyboardEvent) => {
      if (stack[stack.length - 1] !== token) return;
      if (e.key === "Escape") {
        if (!latest.current.closable) return;
        e.preventDefault();
        latest.current.onClose();
        return;
      }
      if (e.key !== "Tab" || !box.current) return;
      const stops = Array.from(box.current.querySelectorAll<HTMLElement>(FOCUSABLE))
        .filter((el) => el.getAttribute("aria-hidden") !== "true");
      if (stops.length === 0) {
        e.preventDefault();
        return;
      }
      const first = stops[0];
      const last = stops[stops.length - 1];
      const active = document.activeElement;
      const outside = !box.current.contains(active);
      if (e.shiftKey && (active === first || outside)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (active === last || outside)) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      stack.splice(stack.indexOf(token), 1);
    };
  }, [token]);

  // IN ON ARRIVAL, BACK OUT ON LEAVING — unless the reader has already moved
  // focus somewhere else themselves, which is theirs to keep. The page under
  // the popup does not scroll while it is up.
  useEffect(() => {
    // Held in locals: the ref is null again by the time the cleanup runs, and
    // "is focus still inside the box" is a question about the box that WAS up.
    const el = box.current;
    const fallback = fallbackFocusRef?.current ?? null;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    if (el && !el.contains(document.activeElement)) {
      const asked = typeof initialFocus === "string" ? el.querySelector<HTMLElement>(initialFocus) : initialFocus?.current;
      (asked ?? el).focus();
    }

    return () => {
      document.body.style.overflow = overflow;
      const active = document.activeElement;
      const movedOn = active && active !== document.body && active !== document.documentElement && !el?.contains(active);
      if (movedOn) return;
      const back = returnFocusTo ? document.querySelector<HTMLElement>(returnFocusTo) : opener;
      const target = back?.isConnected && back !== document.body ? back : fallback;
      if (target?.isConnected) target.focus();
    };
    // Once per opening: what to focus is decided when the box comes up.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      className={clsx("fixed inset-0 flex bg-scrim/60", placement === "right" ? "justify-end" : "items-center justify-center p-4", layer)}
      data-testid={backdropTestId}
      // THE BACKDROP CLOSES ONLY ON ITSELF, and only when the press STARTED on
      // it: a drag that began inside the box and ended out here is not a
      // dismissal.
      //
      // ⚠️ AND IT CANCELS THE PRESS. A mousedown's default action is to move focus
      // — to the body, on a backdrop — and it runs AFTER this handler, so the
      // focus the frame had just handed back to the opener was taken away again.
      onMouseDown={(e) => {
        if (e.target !== e.currentTarget || !latest.current.closable) return;
        e.preventDefault();
        latest.current.onClose();
      }}
      // Clicks inside a popup are the popup's. Without this they bubble through
      // React to whatever rendered it — a comment card that pins on click.
      onClick={(e) => e.stopPropagation()}
    >
      {edges && placement === "center" && edges.left}
      <div
        ref={box}
        role={role}
        aria-modal="true"
        aria-labelledby={labelledBy}
        aria-describedby={describedBy}
        tabIndex={-1}
        data-testid={testId}
        className={clsx("w-full min-w-0 outline-none", className)}
      >
        {children}
      </div>
      {edges && placement === "center" && edges.right}
    </div>
  );
}
